import { ConnectedComponentProps } from '../types-uischema';
import * as React from 'react';
import {error} from '../../log';
import { debounceRender } from 'ui/common/debounce-render';
import { AppSetting } from 'dependencies/appsettings';
import { scrollIntoView } from 'app/infrastructure/platform/browser-dom';
import styles from './fallback.scss';

const defaultObj = {};
/**
 * Provide a placeholder if a component fails to render
 *
 * Show a inspect button if componentDebug is enable
 *
 * @export
 * @param {*} component
 */
// eslint:disable-next-line
export function withFallback(Component : React.ComponentClass<ConnectedComponentProps>) :
React.ComponentClass<ConnectedComponentProps>{

  /**
   * Internal Higher Order Component that provides a wrapping context and error messages for react components
   * that fails to render
   */
  class FallbackProvider extends React.Component<ConnectedComponentProps, any > {
    private showComponentInspector: boolean = !!AppSetting.ComponentDebug;
    private containerRef = React.createRef<HTMLDivElement>();
    public state: Readonly<any> = {
      preview: {}
    };

    public componentDidCatch(err: any) {
      this.setState({
        error: err
      });

      error(err);

      console.log('Failed to render', Component.name || Component.displayName, Component, this.props); // eslint:disable-line
    }

    public inspect = () => {
      this.props.actions.inspect(this.props);
      this.focusInspected();
    };

    public inspectError = () => {
      this.props.actions.inspect(this.props, this.state.error);
      this.focusInspected();
    };

    private focusInspected = () => {
      if (this.containerRef.current) {
        scrollIntoView(this.containerRef.current);
        window.scroll(0, window.scrollY - 30);
      }
    };

    private renderErrorPlaceholder = (componentName: string, id: any, isBeeingInspected: boolean) => {

       return (
          <div ref={this.containerRef} className={`form-group ${styles.popoverWrapper} ${isBeeingInspected && styles.inspectionMode}`}>

            <div className={ styles.popoverTitle } onClick={this.inspectError} title="Click to view details">
            { `⚠️ Component ${componentName} ${id} is not properly configured` }
            </div>

          </div>);
    };

    private renderWithInspector = (isBeeingInspected: boolean) => {

      const inspectorStyle = {
        fontSize: '18px',
        marginRight: '7px',
        border: this.state.inspected ? '1px dashed blue' : ''
      };

      return (
        <div ref={this.containerRef} className={`${styles.popoverWrapper} ${isBeeingInspected && styles.inspectionMode}`} >

          <span title="Click to inspect"
                className="pull-left inspect-cogwheel"
                style={inspectorStyle}
                onClick={this.inspect}>⚙</span>

          <Component {...{...this.props, ...this.props.preview}} />

        </div>);
    };

    /**
     * When componentDebug flag is set, allow any valid component to be inspected
     * Having componentName indicates that this domains ControlType maps to a configured ui-component.
     * If not a debug renderer is beeing used and inspection is meaningless
     */
    public render(){

      const {componentName, id} = this.props.componentDescriptor || defaultObj as any;
      const componentCanBeInspected = this.showComponentInspector && !!componentName;
      const isBeeingInspected = this.props.$inspectedComponent &&
                                this.props.$inspectedComponent.componentDescriptor.id === id;

      if ( this.state.error ){
        return this.renderErrorPlaceholder(componentName, id, isBeeingInspected);
      }

      if (componentCanBeInspected){
        return  this.renderWithInspector(isBeeingInspected);
      }

      return <Component {...this.props} />;
    }

  }

  return debounceRender(FallbackProvider) as any;
}
